import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { FileDown, FileSpreadsheet, FileText, Loader2, TrendingUp } from "lucide-react";
import DashboardLayout from "@/components/DashboardLayout";
import { trpc } from "@/lib/trpc";
import { useState } from "react";
import { toast } from "sonner";
import { useTranslation } from 'react-i18next';

export default function Reports() {
  const { t } = useTranslation();
  const [selectedPeriod, setSelectedPeriod] = useState<string>("");

  // Fetch periods
  const { data: periods, isLoading: loadingPeriods } = trpc.periods.list.useQuery();

  // Export mutations
  const exportPDFMutation = trpc.reports.exportPDF.useMutation({
    onSuccess: (data) => {
      if (data.url) {
        window.open(data.url, '_blank');
        toast.success(t('reports.pdf_generated_successfully'));
      }
    },
    onError: (error) => {
      toast.error(t('reports.failed_to_generate_pdf', { error: error.message }));
    },
  });

  const exportCSVMutation = trpc.reports.exportCSV.useMutation({
    onSuccess: (data) => {
      if (data.url) {
        window.open(data.url, '_blank');
        toast.success(t('reports.csv_generated_successfully'));
      }
    },
    onError: (error) => {
      toast.error(t('reports.failed_to_generate_csv', { error: error.message }));
    },
  });

  const exportExcelMutation = trpc.reports.exportExcel.useMutation({
    onSuccess: (data) => {
      if (data.url) {
        window.open(data.url, '_blank');
        toast.success(t('reports.excel_generated_successfully'));
      }
    },
    onError: (error) => {
      toast.error(t('reports.failed_to_generate_excel', { error: error.message }));
    },
  });

  const handleExportPDF = () => {
    if (!selectedPeriod) {
      toast.error(t('reports.please_select_period'));
      return;
    }
    exportPDFMutation.mutate({ periodId: selectedPeriod });
  };

  const handleExportCSV = () => {
    if (!selectedPeriod) {
      toast.error(t('reports.please_select_period'));
      return;
    }
    exportCSVMutation.mutate({ periodId: selectedPeriod });
  };

  const handleExportExcel = () => {
    if (!selectedPeriod) {
      toast.error(t('reports.please_select_period'));
      return;
    }
    exportExcelMutation.mutate({ periodId: selectedPeriod });
  };

  const handleGenerateTemplate = (template: string) => {
    if (!selectedPeriod) {
      toast.error(t('reports.please_select_period'));
      return;
    }
    
    // Use PDF export for all templates for now
    exportPDFMutation.mutate({ 
      periodId: selectedPeriod,
      template 
    });
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t('reports.title')}</h1>
          <p className="text-muted-foreground">
            {t('reports.subtitle')}
          </p>
        </div>

        {/* Period Selection */}
        <Card>
          <CardHeader>
            <CardTitle>{t('reports.select_period')}</CardTitle>
            <CardDescription>
              {t('reports.select_period_desc')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loadingPeriods ? (
              <div className="flex items-center gap-2 text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                {t('reports.loading_periods')}...
              </div>
            ) : periods && periods.length > 0 ? (
              <Select value={selectedPeriod} onValueChange={setSelectedPeriod}>
                <SelectTrigger className="w-full md:w-[300px]">
                  <SelectValue placeholder={t('reports.select_period_placeholder')} />
                </SelectTrigger>
                <SelectContent>
                  {periods.map((period) => (
                    <SelectItem key={period.id} value={period.id}>
                      {period.label || period.period_label || `${period.year} ${period.month || ''}`.trim()}{" "}
                      <span className="text-xs text-muted-foreground">
                        ({period.status === 'closed' ? "closed" : "open"})
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <div className="text-sm text-muted-foreground">
                {t('reports.no_periods_available')}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Export Options */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-primary" />
                <CardTitle>{t('reports.pdf_report')}</CardTitle>
              </div>
              <CardDescription>
                {t('reports.pdf_report_desc')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button 
                onClick={handleExportPDF} 
                className="w-full bg-gradient-mypa hover:opacity-90"
                disabled={!selectedPeriod || exportPDFMutation.isPending}
              >
                {exportPDFMutation.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t('reports.generating')}...
                  </>
                ) : (
                  <>
                    <FileDown className="mr-2 h-4 w-4" />
                    {t('reports.download_pdf')}
                  </>
                )}
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <FileSpreadsheet className="h-5 w-5 text-primary" />
                <CardTitle>{t('reports.csv_export')}</CardTitle>
              </div>
              <CardDescription>
                {t('reports.csv_export_desc')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button 
                onClick={handleExportCSV} 
                className="w-full" 
                variant="outline"
                disabled={!selectedPeriod || exportCSVMutation.isPending}
              >
                {exportCSVMutation.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t('reports.generating')}...
                  </>
                ) : (
                  <>
                    <FileDown className="mr-2 h-4 w-4" />
                    {t('reports.download_csv')}
                  </>
                )}
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <FileSpreadsheet className="h-5 w-5 text-primary" />
                <CardTitle>{t('reports.excel_report')}</CardTitle>
              </div>
              <CardDescription>
                {t('reports.excel_report_desc')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button 
                onClick={handleExportExcel} 
                className="w-full" 
                variant="outline"
                disabled={!selectedPeriod || exportExcelMutation.isPending}
              >
                {exportExcelMutation.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t('reports.generating')}...
                  </>
                ) : (
                  <>
                    <FileDown className="mr-2 h-4 w-4" />
                    {t('reports.download_excel')}
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Report Templates */}
        <Card>
          <CardHeader>
            <CardTitle>{t('reports.report_templates')}</CardTitle>
            <CardDescription>
              {t('reports.report_templates_desc')}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div className="flex items-center gap-3">
                <TrendingUp className="h-5 w-5 text-muted-foreground" />
                <div>
                  <p className="font-medium">{t('reports.monthly_summary')}</p>
                  <p className="text-sm text-muted-foreground">
                    {t('reports.monthly_summary_desc')}
                  </p>
                </div>
              </div>
              <Button 
                size="sm"
                onClick={() => handleGenerateTemplate('monthly')}
                disabled={!selectedPeriod || exportPDFMutation.isPending}
              >
                {exportPDFMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  t('reports.generate')
                )}
              </Button>
            </div>

            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div className="flex items-center gap-3">
                <TrendingUp className="h-5 w-5 text-muted-foreground" />
                <div>
                  <p className="font-medium">{t('reports.quarterly_report')}</p>
                  <p className="text-sm text-muted-foreground">
                    {t('reports.quarterly_report_desc')}
                  </p>
                </div>
              </div>
              <Button 
                size="sm"
                onClick={() => handleGenerateTemplate('quarterly')}
                disabled={!selectedPeriod || exportPDFMutation.isPending}
              >
                {exportPDFMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  t('reports.generate')
                )}
              </Button>
            </div>

            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div className="flex items-center gap-3">
                <TrendingUp className="h-5 w-5 text-muted-foreground" />
                <div>
                  <p className="font-medium">{t('reports.annual_report')}</p>
                  <p className="text-sm text-muted-foreground">
                    {t('reports.annual_report_desc')}
                  </p>
                </div>
              </div>
              <Button 
                size="sm"
                onClick={() => handleGenerateTemplate('annual')}
                disabled={!selectedPeriod || exportPDFMutation.isPending}
              >
                {exportPDFMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  t('reports.generate')
                )}
              </Button>
            </div>

            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div className="flex items-center gap-3">
                <TrendingUp className="h-5 w-5 text-muted-foreground" />
                <div>
                  <p className="font-medium">{t('reports.cbam_report')}</p>
                  <p className="text-sm text-muted-foreground">
                    {t('reports.cbam_report_desc')}
                  </p>
                </div>
              </div>
              <Button 
                size="sm"
                onClick={() => handleGenerateTemplate('cbam')}
                disabled={!selectedPeriod || exportPDFMutation.isPending}
              >
                {exportPDFMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  t('reports.generate')
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Info Card */}
        {!selectedPeriod && (
          <Card className="bg-muted/50">
            <CardContent className="pt-6">
              <p className="text-sm text-muted-foreground text-center">
                {t('reports.select_period_to_generate')}
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
}

